/**
 * API エラーをユーザー向けの日本語に変換する（#765）。
 *
 * 生の `FetchError: [POST] ".../events/trigger": 413` をそのまま見せると、
 * 何が悪いのか・次に何をすればよいのかが分からない。2026-08-01 の実例では、
 * この不透明さが原因で**まったく無関係なアセット削減（450MB → 86MB → 28MB）に
 * 時間が溶けた**（実際の原因は表紙画像の送信方式だった → #762）。
 *
 * 解決の優先順:
 *
 * 1. サーバが返した `errorCode`（`ERROR_MESSAGES`）— 原因が確定しているもの
 * 2. HTTP ステータス（`STATUS_MESSAGES`）— サーバまで届かなかった / 分類前の失敗
 * 3. 呼び出し側が渡した fallback
 *
 * **文言はここにしか置かない。** サーバ側は `errorCode` だけを返す（同じ状況の
 * 文言が 2 箇所に散ると、片方だけ直る）。
 */

import { UserFacingError } from '~/types/errors'

/** `errorCode` → ユーザー向け文言。原因と**次の一手**をセットで書くこと。 */
const ERROR_MESSAGES: Record<string, string> = {
  // --- GitHub（黒い魔導書） ---
  GITHUB_NOT_CONNECTED: 'GitHub とつながっていません。ゆりかごから接続してからお試しください。',
  GITHUB_TOKEN_REVOKED: 'GitHub との接続が切れています。もう一度つなぎ直してください。',
  GITHUB_REPO_NOT_FOUND: 'リポジトリが見つかりませんでした。名前とブランチをご確認ください。',
  GITHUB_REPO_INVALID: 'リポジトリの指定が正しくないようです。名前をご確認ください。',
  GITHUB_RATE_LIMITED: 'GitHub へのアクセスが混み合っています。少し時間をおいてお試しください。',
  GITHUB_FETCH_FAILED: 'GitHub からの取り込みに失敗しました。少し時間をおいてお試しください。',
  NO_PYXEL_FILES: 'このリポジトリには Pyxel の魔導書が見つかりませんでした。',

  // --- ビルド / 公開 ---
  BUILD_FAILED: '魔導書の組み立てに失敗しました。エントリーポイントのファイルをご確認ください。',
  COVER_UPLOAD_FAILED: '表紙の絵をうまく受け取れませんでした。もう一度撮り直してみてください。',
  NOT_ENOUGH_MP: '魔導エネルギーが足りません。たまってからお試しください。',
  SLUG_IN_USE: 'そのスラッグはすでに使われています。別のものをお選びください。',

  // --- 魔導書のファイル操作（既存の errorCode。#727 / #734） ---
  MEDIA_TOO_LARGE: 'ファイルが大きすぎます。もっと小さいものをお試しください。',
  MEDIA_CONTENT_INVALID: 'ファイルの中身を読み取れませんでした。形式をご確認ください。',
  LAST_PY_FILE_REQUIRED: '魔導書には .py が最低 1 つ必要です。',
}

/**
 * HTTP ステータス → 文言。`errorCode` が無い（= ハンドラまで届かなかった）失敗用。
 *
 * 413 と 504 が特に効く。どちらも「作品が大きい」と結びつきやすく、生のまま
 * 見せると原因の推測を誤らせる。
 */
const STATUS_MESSAGES: Record<number, string> = {
  401: 'サインインの有効期限が切れたようです。もう一度サインインしてください。',
  403: 'この操作を行う権限がありません。',
  404: '対象が見つかりませんでした。',
  413: '送ろうとしたデータが大きすぎました。表紙のキャプチャを短くするか、画面サイズを小さくしてお試しください。',
  429: 'アクセスが混み合っています。少し時間をおいてお試しください。',
  502: 'サーバとの通信に失敗しました。少し時間をおいてお試しください。',
  503: 'ただいま混み合っています。少し時間をおいてお試しください。',
  504: '処理に時間がかかりすぎました。作品が大きい場合は、しばらく置いてもう一度お試しください。',
}

const GENERIC_FALLBACK = 'うまくいきませんでした。少し時間をおいてお試しください。'

interface FetchLikeError {
  status?: number
  statusCode?: number
  data?: unknown
}

/** `$fetch` が投げるエラーから `errorCode` を取り出す（無ければ null）。 */
export function extractErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null
  const data = (err as FetchLikeError).data
  if (typeof data !== 'object' || data === null) return null
  const code = (data as { errorCode?: unknown }).errorCode
  return typeof code === 'string' ? code : null
}

/** `$fetch` が投げるエラーから HTTP ステータスを取り出す（無ければ null）。 */
export function extractStatus(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as FetchLikeError
  const status = e.status ?? e.statusCode
  return typeof status === 'number' ? status : null
}

/**
 * ユーザーに見せる 1 行を返す。**生の `FetchError` / HTTP ステータスは絶対に出さない。**
 *
 * @param fallback その画面固有の言い回し。省略時は汎用文。
 */
export function toUserMessage(err: unknown, fallback = GENERIC_FALLBACK): string {
  // クライアント側で検出した失敗は文面が確定している（実測サイズ入りなど）。
  if (err instanceof UserFacingError) return err.message

  const code = extractErrorCode(err)
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]

  const status = extractStatus(err)
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status]

  return fallback
}
